import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { 
  GraduationCap, CreditCard, Lock, CheckCircle, ArrowLeft,
  Truck, Headphones, Briefcase, Building2, AlertCircle, Copy, Check
} from 'lucide-react';
import { COURSES, BUNDLE_INFO, ACADEMY_INFO } from '../courseData';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping
const ICON_MAP = {
  Truck,
  Headphones,
  Briefcase,
  Building2
};

const AcademyCheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get purchase intent from navigation state or URL params
  const searchParams = new URLSearchParams(location.search);
  const purchaseType = location.state?.purchaseType || searchParams.get('type') || 'bundle';
  const courseId = location.state?.courseId || searchParams.get('course');
  
  const [selectedType, setSelectedType] = useState(purchaseType);
  const [selectedCourse, setSelectedCourse] = useState(courseId || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
  });

  // Calculate total
  const getTotal = () => {
    if (selectedType === 'bundle') {
      return BUNDLE_INFO.price;
    }
    const course = COURSES.find(c => c.id === selectedCourse);
    return course?.price || 0;
  };

  // Get selected courses
  const getSelectedCourses = () => {
    if (selectedType === 'bundle') {
      return COURSES;
    }
    return COURSES.filter(c => c.id === selectedCourse);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      setFormData(prev => ({ ...prev, [name]: formatted.slice(0, 19) }));
      return;
    }
    
    // Format expiry as MM/YY
    if (name === 'expiry') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length >= 2) {
        setFormData(prev => ({ ...prev, [name]: cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) }));
      } else {
        setFormData(prev => ({ ...prev, [name]: cleaned }));
      }
      return;
    }
    
    // CVC max 4 digits
    if (name === 'cvc') {
      setFormData(prev => ({ ...prev, [name]: value.slice(0, 4) }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    // Basic validation
    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields');
      setIsProcessing(false);
      return;
    }

    if (selectedType === 'individual' && !selectedCourse) {
      setError('Please select a course');
      setIsProcessing(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/academy/purchase`, {
        email: formData.email,
        name: formData.name,
        purchase_type: selectedType,
        course_id: selectedType === 'individual' ? selectedCourse : null,
        card_last_four: formData.cardNumber.replace(/\s/g, '').slice(-4) || '4242',
      });

      if (response.data.success) {
        setSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Purchase failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyAccessCode = () => {
    if (success?.access_code) {
      navigator.clipboard.writeText(success.access_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-20" data-testid="checkout-success">
        <Helmet>
          <title>Purchase Complete | Queen B Academy</title>
        </Helmet>
        
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-8 text-center">
            {/* Success icon */}
            <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-2">
              Purchase Complete!
            </h1>
            <p className="text-white/60 mb-8">
              Your courses are ready to be unlocked.
            </p>
            
            {/* Access Code */}
            <div className="bg-[#121212] border border-white/10 p-6 mb-8">
              <p className="text-white/40 text-sm uppercase tracking-wider mb-2">Your Access Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl font-mono font-bold text-[#D4AF37]" data-testid="access-code">
                  {success.access_code}
                </span>
                <button
                  onClick={copyAccessCode}
                  className="p-2 bg-white/5 hover:bg-white/10 transition-colors"
                  title="Copy code"
                >
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-white/60" />}
                </button>
              </div>
              <p className="text-white/40 text-sm mt-4">
                Save this code! You'll need it to unlock your courses in The Hive.
              </p>
            </div>
            
            {/* Order Details */}
            <div className="bg-[#121212] border border-white/10 p-6 mb-8 text-left">
              <h3 className="text-white font-bold mb-4">Order Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Order ID</span>
                  <span className="text-white font-mono">{success.purchase_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Amount Paid</span>
                  <span className="text-[#D4AF37] font-bold">${success.total_amount}</span>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <p className="text-white/60 text-sm mb-2">Courses Included:</p>
                  {success.courses.map(courseId => {
                    const course = COURSES.find(c => c.id === courseId);
                    return (
                      <div key={courseId} className="flex items-center gap-2 text-sm text-white/80 py-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {course?.title || courseId}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Email Confirmation */}
            <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div className="text-left">
                <p className="text-green-400 font-bold text-sm">Confirmation Email Sent!</p>
                <p className="text-white/60 text-xs">Your access code and instructions have been sent to {formData.email}</p>
              </div>
            </div>
            
            {/* Next Steps */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 mb-8">
              <h3 className="text-[#D4AF37] font-bold mb-2">Next Steps</h3>
              <ol className="text-left text-white/70 text-sm space-y-2">
                <li>1. Log in to The Hive (or create an account)</li>
                <li>2. Go to the Academy section</li>
                <li>3. Enter your access code: <span className="text-[#D4AF37] font-mono">{success.access_code}</span></li>
                <li>4. Start learning!</li>
              </ol>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/hive/access"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
                data-testid="go-to-hive-btn"
              >
                <GraduationCap className="w-5 h-5" />
                Go to The Hive
              </Link>
              <Link
                to="/academy"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-bold uppercase tracking-wider hover:bg-white/20 transition-colors"
              >
                Back to Academy
              </Link>
            </div>
            
            {/* Mock Payment Notice */}
            <p className="text-white/30 text-xs mt-8">
              * This is a demonstration purchase. No actual payment was processed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-20" data-testid="academy-checkout">
      <Helmet>
        <title>Checkout | Queen B Academy</title>
      </Helmet>
      
      <div className="max-w-6xl mx-auto px-6">
        {/* Back Link */}
        <Link
          to="/academy"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Academy
        </Link>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Order Selection */}
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-6">
              Complete Your <span className="text-[#D4AF37]">Purchase</span>
            </h1>
            
            {/* Purchase Type Toggle */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setSelectedType('bundle')}
                className={`flex-1 py-3 px-4 font-bold uppercase text-sm transition-colors ${
                  selectedType === 'bundle'
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
                data-testid="select-bundle"
              >
                Bundle (Save $200)
              </button>
              <button
                onClick={() => setSelectedType('individual')}
                className={`flex-1 py-3 px-4 font-bold uppercase text-sm transition-colors ${
                  selectedType === 'individual'
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
                data-testid="select-individual"
              >
                Individual Course
              </button>
            </div>
            
            {/* Course Selection (for individual) */}
            {selectedType === 'individual' && (
              <div className="mb-6">
                <label className="block text-white/60 text-sm mb-3">Select a Course</label>
                <div className="space-y-3">
                  {COURSES.map(course => {
                    const IconComponent = ICON_MAP[course.icon] || GraduationCap;
                    return (
                      <button
                        key={course.id}
                        onClick={() => setSelectedCourse(course.id)}
                        className={`w-full flex items-center gap-4 p-4 border transition-colors text-left ${
                          selectedCourse === course.id
                            ? 'bg-[#D4AF37]/10 border-[#D4AF37]'
                            : 'bg-[#0A0A0A] border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`select-course-${course.id}`}
                      >
                        <div className="p-2" style={{ backgroundColor: `${course.color}20` }}>
                          <IconComponent className="w-5 h-5" style={{ color: course.color }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{course.title}</p>
                          <p className="text-white/40 text-sm">{course.subtitle}</p>
                        </div>
                        <span className="text-[#D4AF37] font-bold">${course.price}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Bundle Display */}
            {selectedType === 'bundle' && (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold">Complete Academy Bundle</h3>
                  <div className="text-right">
                    <span className="text-white/40 line-through text-sm">$1,000</span>
                    <span className="text-[#D4AF37] font-bold text-xl ml-2">${BUNDLE_INFO.price}</span>
                  </div>
                </div>
                <p className="text-white/60 text-sm mb-4">All 4 courses included:</p>
                <div className="space-y-2">
                  {COURSES.map(course => {
                    const IconComponent = ICON_MAP[course.icon] || GraduationCap;
                    return (
                      <div key={course.id} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <IconComponent className="w-4 h-4" style={{ color: course.color }} />
                        <span className="text-white/80 text-sm">{course.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Order Summary */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-4">Order Summary</h3>
              <div className="space-y-3">
                {getSelectedCourses().map(course => (
                  <div key={course.id} className="flex justify-between text-sm">
                    <span className="text-white/70">{course.title}</span>
                    <span className="text-white">${course.price}</span>
                  </div>
                ))}
                {selectedType === 'bundle' && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Bundle Discount</span>
                    <span>-${BUNDLE_INFO.savings}</span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-3 flex justify-between">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-[#D4AF37] font-bold text-xl">${getTotal()}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right: Payment Form */}
          <div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Lock className="w-5 h-5 text-green-500" />
                <span className="text-white font-bold">Secure Checkout</span>
                <span className="text-xs text-white/40 ml-auto">Demo Mode</span>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 mb-6" data-testid="checkout-error">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-400">{error}</span>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div>
                  <h3 className="text-white/60 text-sm uppercase tracking-wider mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                        placeholder="John Smith"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                        placeholder="john@example.com"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Payment Info */}
                <div>
                  <h3 className="text-white/60 text-sm uppercase tracking-wider mb-4">Payment Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Card Number</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none pl-12"
                          placeholder="4242 4242 4242 4242"
                          data-testid="input-card"
                        />
                        <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/60 text-sm mb-2">Expiry Date</label>
                        <input
                          type="text"
                          name="expiry"
                          value={formData.expiry}
                          onChange={handleInputChange}
                          className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                          placeholder="MM/YY"
                          data-testid="input-expiry"
                        />
                      </div>
                      <div>
                        <label className="block text-white/60 text-sm mb-2">CVC</label>
                        <input
                          type="text"
                          name="cvc"
                          value={formData.cvc}
                          onChange={handleInputChange}
                          className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                          placeholder="123"
                          data-testid="input-cvc"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Demo Notice */}
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
                  <p className="text-[#D4AF37] text-sm">
                    <strong>Demo Mode:</strong> This is a mock checkout. No actual payment will be processed. 
                    You'll receive a valid access code to test the Academy flow.
                  </p>
                </div>
                
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing || (selectedType === 'individual' && !selectedCourse)}
                  className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-purchase"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Complete Purchase - ${getTotal()}
                    </>
                  )}
                </button>
                
                {/* Non-Refundable Notice */}
                <div className="bg-red-500/10 border border-red-500/30 p-3">
                  <p className="text-red-400 text-xs text-center font-bold">
                    ALL SALES ARE FINAL. Queen B Academy courses are non-refundable once purchased.
                  </p>
                </div>
                
                <p className="text-white/40 text-xs text-center">
                  By completing this purchase, you agree to our <Link to="/refund-policy" className="text-[#D4AF37] hover:underline">Refund Policy</Link> and Terms of Service.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademyCheckoutPage;
